import { useEffect, useMemo } from 'react';
import { useGameStore } from '@campus/core';
import type { UserStatus } from '@campus/core';
import { API_BASE } from '../../lib/apiBase.js';
import { apiHeaders } from './membersStore.js';

/** Présence d'une personne en ligne, sur n'importe quelle carte du campus. */
export interface OnlinePerson {
  userId: string;
  displayName: string;
  status: UserStatus;
  zoneSlug: string;
  zoneName: string;
}

const POLL_MS = 20_000;
let polling = 0;
let timer: ReturnType<typeof setInterval> | null = null;

export async function refreshCampusPresence(): Promise<void> {
  try {
    const res = await fetch(`${API_BASE}/world/presence/all`, { headers: apiHeaders() });
    if (!res.ok) return;
    const data = await res.json();
    useGameStore.getState().setAllZonePresence(Array.isArray(data) ? data : (data.zones ?? []));
  } catch { /* réseau : on garde la dernière liste */ }
}

/** Rafraîchit la présence de tout le campus tant qu'un composant l'utilise. */
export function useCampusPresencePolling(): void {
  useEffect(() => {
    polling++;
    if (!timer) {
      void refreshCampusPresence();
      timer = setInterval(() => { void refreshCampusPresence(); }, POLL_MS);
    }
    return () => {
      polling--;
      if (polling === 0 && timer) { clearInterval(timer); timer = null; }
    };
  }, []);
}

/**
 * Personnes en ligne sur tout le campus : la carte courante en direct
 * (Colyseus), les autres d'après la présence Redis (rafraîchie toutes les 20 s).
 */
export function useCampusOnline(): Map<string, OnlinePerson> {
  const zonePlayers = useGameStore((s) => s.zonePlayers);
  const allZonePresence = useGameStore((s) => s.allZonePresence);
  const currentZoneId = useGameStore((s) => s.currentZoneId);
  const currentZoneName = useGameStore((s) => s.currentZoneName);
  return useMemo(() => {
    const currentSlug = currentZoneId?.split(':')[1] ?? '';
    const online = new Map<string, OnlinePerson>();
    for (const p of zonePlayers.values()) {
      online.set(p.userId, { userId: p.userId, displayName: p.displayName, status: p.status, zoneSlug: currentSlug, zoneName: currentZoneName ?? '' });
    }
    for (const zone of allZonePresence) {
      if (zone.zoneSlug === currentSlug) continue;
      for (const p of zone.players) {
        if (!online.has(p.userId)) {
          online.set(p.userId, { userId: p.userId, displayName: p.displayName, status: p.status as UserStatus, zoneSlug: zone.zoneSlug, zoneName: zone.zoneName });
        }
      }
    }
    return online;
  }, [zonePlayers, allZonePresence, currentZoneId, currentZoneName]);
}
